import { Fragment } from "react";

const BASE_GLOW_PATTERN = /(?:おぺにす|open\s*is)(?:\.{3,}|…+|．{3,}|・{3,})/gi;
const EXTENDED_EFFECT_PATTERN =
  /(?<glow>(?:おぺにす|open\s*is\b|お(?:ゔ|ヴ)ぁぎな|おまんこ)(?:\.{3,}|…+|．{3,}|・{3,})?)|(?<red>こりゃ赤い(?:\.{3,}|…+|．{3,}|・{3,})?)|(?<rainbow>レインボー|rainbow\b|ゲーミング)/gi;
const HUE_CYCLE_DEGREES = 360;
const RAINBOW_HUE_STEP_DEGREES = 36;
const RAINBOW_SATURATION_PERCENT = 90;
const RAINBOW_DARK_LIGHTNESS_PERCENT = 60;
const RAINBOW_LIGHT_LIGHTNESS_PERCENT = 40;

interface Props {
  extended?: boolean;
  isLightMode?: boolean;
  offset?: number;
  rainbow?: boolean;
  text: string;
}

function rainbowChars(
  text: string,
  offset: number,
  isLightMode: boolean,
): React.ReactNode {
  const lightness = isLightMode
    ? RAINBOW_LIGHT_LIGHTNESS_PERCENT
    : RAINBOW_DARK_LIGHTNESS_PERCENT;

  return Array.from(text).map((character, index) => (
    <span
      // biome-ignore lint/suspicious/noArrayIndexKey: 文字列とoffsetは同一描画中に並び替わらず、各spanは状態を持たない
      key={`${offset}-${index}`}
      style={{
        color: `hsl(${((offset + index) * RAINBOW_HUE_STEP_DEGREES) % HUE_CYCLE_DEGREES}, ${RAINBOW_SATURATION_PERCENT}%, ${lightness}%)`,
      }}
    >
      {character}
    </span>
  ));
}

function codePointLength(text: string): number {
  return Array.from(text).length;
}

export const TextEffects: React.FunctionComponent<Props> = ({
  extended = false,
  isLightMode = false,
  offset = 0,
  rainbow = false,
  text,
}: Props) => {
  const pattern = new RegExp(
    extended ? EXTENDED_EFFECT_PATTERN.source : BASE_GLOW_PATTERN.source,
    "gi",
  );
  const matches = Array.from(text.matchAll(pattern));
  const renderChunk = (chunk: string, offset: number): React.ReactNode =>
    rainbow ? rainbowChars(chunk, offset, isLightMode) : chunk;

  if (matches.length === 0) return <>{renderChunk(text, offset)}</>;

  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let renderedLength = 0;
  for (const match of matches) {
    const start = match.index ?? cursor;
    if (start > cursor) {
      nodes.push(
        <Fragment key={`text-${cursor}`}>
          {renderChunk(text.slice(cursor, start), offset + renderedLength)}
        </Fragment>,
      );
    }

    const className = match.groups?.red
      ? "text-red-glow"
      : match.groups?.rainbow
        ? "text-rainbow-highlight"
        : "text-glow";
    nodes.push(
      <span key={`effect-${start}`} className={className}>
        {renderChunk(
          match[0],
          offset + renderedLength + codePointLength(text.slice(cursor, start)),
        )}
      </span>,
    );
    renderedLength +=
      codePointLength(text.slice(cursor, start)) + codePointLength(match[0]);
    cursor = start + match[0].length;
  }

  if (cursor < text.length) {
    nodes.push(
      <Fragment key={`text-${cursor}`}>
        {renderChunk(text.slice(cursor), offset + renderedLength)}
      </Fragment>,
    );
  }
  return <>{nodes}</>;
};
